import {
  createPaymentBodySchema,
  intervalUnitSchema,
  paymentKindSchema,
} from '@klopotun/contracts';
import { z } from 'zod';
import {
  PAST_PAYMENT_STATE,
  PAYMENT_FREQUENCIES,
  type PaymentFormValues,
} from '../model/payment-form-values.js';
import { toPaymentBodyDraft } from './payment-form-adapter.js';

type Complaint = {
  field: keyof PaymentFormValues;
  message: string;
};

/**
 * The server answers a rejected body with one sentence and no field names, so this table is the
 * only place a person ever learns which field is wrong. Every key of the payment body is listed:
 * an unmapped path would fall back to a message on a field it has nothing to do with.
 */
const COMPLAINT_BY_BODY_FIELD: Record<string, Complaint> = {
  name: { field: 'name', message: 'Вкажіть назву підписки' },
  kind: { field: 'kind', message: 'Оберіть спосіб оплати' },
  amountMode: { field: 'isVariableAmount', message: 'Оберіть, чи сума щоразу інша' },
  amountMinor: { field: 'amount', message: 'Вкажіть суму — число більше нуля, наприклад 299.00' },
  currency: { field: 'currency', message: 'Оберіть валюту' },
  intervalUnit: { field: 'customIntervalUnit', message: 'Оберіть одиницю періоду' },
  intervalCount: { field: 'customIntervalCount', message: 'Кратність — ціле число, більше нуля' },
  anchorDay: { field: 'startDate', message: 'Вкажіть дату списання' },
  anchorMonth: { field: 'startDate', message: 'Вкажіть дату списання' },
  startDate: { field: 'startDate', message: 'Вкажіть дату списання' },
  pastPayments: { field: 'history', message: 'Перевірте суми минулих платежів' },
  skippedDates: { field: 'history', message: 'Перевірте позначки минулих платежів' },
  endDate: {
    field: 'endDate',
    message: 'Кінцева дата не може бути раніше за дату списання',
  },
  cardId: { field: 'cardId', message: 'Оберіть картку зі списку' },
  categoryId: { field: 'categoryId', message: 'Оберіть категорію зі списку' },
  cancelUrl: { field: 'cancelUrl', message: 'Посилання має починатися з http:// або https://' },
  note: { field: 'note', message: 'Нотатка задовга або містить недопустимі символи' },
};

const FALLBACK_COMPLAINT: Complaint = {
  field: 'name',
  message: 'Дані форми не пройшли перевірку',
};

const complaintFor = (path: PropertyKey[]): Complaint => {
  const [firstSegment] = path;

  if (typeof firstSegment !== 'string') {
    return FALLBACK_COMPLAINT;
  }

  return COMPLAINT_BY_BODY_FIELD[firstSegment] ?? FALLBACK_COMPLAINT;
};

const paymentFormValuesSchema = z.object({
  name: z.string(),
  amount: z.string(),
  currency: z.string(),
  isVariableAmount: z.boolean(),
  kind: paymentKindSchema,
  frequency: z.enum(PAYMENT_FREQUENCIES),
  customIntervalUnit: intervalUnitSchema,
  customIntervalCount: z.string(),
  startDate: z.string(),
  history: z.array(
    z.object({
      dueDate: z.string(),
      state: z.enum(PAST_PAYMENT_STATE),
      amount: z.string().nullable(),
      isEdited: z.boolean(),
    }),
  ),
  cardId: z.string(),
  categoryId: z.string(),
  endDate: z.string(),
  cancelUrl: z.string(),
  note: z.string(),
});

const checkAgainstContract = (values: PaymentFormValues, ctx: z.RefinementCtx): void => {
  const checked = createPaymentBodySchema.safeParse(toPaymentBodyDraft(values));

  if (checked.success) {
    return;
  }

  for (const issue of checked.error.issues) {
    const complaint = complaintFor(issue.path);

    ctx.addIssue({ code: 'custom', path: [complaint.field], message: complaint.message });
  }
};

export const paymentFormSchema = paymentFormValuesSchema.superRefine(checkAgainstContract);
